/**
 * CharacterIllustration — Web implementation.
 *
 * Renders the optimised SVG asset for the requested name + appearance through
 * an <img>, so the artwork ships as a static asset rather than inlined markup.
 * The asset URL comes from the static source map in sources.web.ts — a literal
 * require() per shipped file, which is the only form webpack (apps/web via
 * Next) and Vite both resolve to an emitted asset. A template `new URL(...,
 * import.meta.url)` is Vite-only: webpack cannot rewrite it and substitutes
 * the build machine's file:// path instead. Width comes from the size prop;
 * height follows the artwork's intrinsic aspect ratio.
 *
 * CSS cannot reach inside an <img>, so the tint state of the appearance prop
 * cannot be honoured here: the untinted artwork renders and a dev-time warning
 * says the tint did not apply — see the guard below.
 */

import React from 'react';

import { getCharacterIllustrationAssetFile } from '../../elements/illustrations';
import type { CharacterIllustrationInputProps } from './CharacterIllustration';
import {
  getIntrinsicAspectRatio,
  isRegisteredIllustration,
  resolveAppearance,
  sizeToWidth,
} from './CharacterIllustration';
import { characterIllustrationSources } from './sources.web';
import { useIllustrationWarning } from './useIllustrationWarning';

export function CharacterIllustration({
  name,
  appearance,
  colour,
  size = 'M',
  accessibilityLabel,
}: CharacterIllustrationInputProps) {
  const registered = isRegisteredIllustration(name);
  const { variant, tint, droppedTint } = registered
    ? resolveAppearance(name, { appearance, colour })
    : ({ variant: 'colour', tint: null, droppedTint: 'unknown-name' } as const);
  // Web cannot honour a tint at all, so a tint that resolution *did* accept is
  // still dropped here — for a platform reason rather than a semantic one.
  useIllustrationWarning(name, tint !== null ? 'platform-unsupported' : droppedTint);

  if (!registered) {
    return null;
  }

  const file = getCharacterIllustrationAssetFile(name, variant);
  const aspectRatio = getIntrinsicAspectRatio(name);
  const sizing: React.CSSProperties =
    size === 'Full' ? { width: '100%' } : { width: sizeToWidth[size] };
  const labelled = accessibilityLabel !== undefined && accessibilityLabel !== '';

  return (
    <img
      src={characterIllustrationSources[file]}
      alt={labelled ? accessibilityLabel : ''}
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      data-testid={`character-illustration-${name}`}
      style={{
        ...sizing,
        aspectRatio,
        height: 'auto',
        display: 'block',
        objectFit: 'contain',
      }}
    />
  );
}
